import React from "react";
import { Field, TextField, NumberField, LongText, RadioField, CheckboxGroupField, InlineCheck, InfoPanel, SectionTitle } from "./fields";
import { FileUpload } from "./FileUpload";
import { Reveal, useSequentialReveal } from "./reveal";
import { HEARING_TYPES, MAX_UPLOAD_HINT } from "../../lib/options";
import { INTAKE } from "../../constants/testIds";
import { Button } from "../ui/button";
import { useBackGuard, hasAnswers } from "./useBackGuard";
import { RequiredDatesNote } from "./RequiredDatesNote";

export const RouteA = ({ form, set, config, onBack, onSubmit, submitting }) => {
  const d = form.routeA || {};
  const u = (k, v) => set("routeA", { ...d, [k]: v });
  const maxMb = config?.max_file_size_mb || 10;
  const { requestBack, dialog } = useBackGuard(hasAnswers(d), onBack);
  const missingDates = (!d.hearing_date && !d.hearing_date_unknown) ? ["a-hearing-date"] : [];
  const datesOk = missingDates.length === 0;
  const otherOk = d.hearing_type !== "Other — please state" || !!(d.hearing_type_other && d.hearing_type_other.trim());
  const prepOk = d.wants_prep !== "Yes" || (d.prep_scope || []).length > 0;

  const csFlags = [
    !!d.what_happened,
    !!d.help_wanted,
    !!d.next_deadline || !!d.next_deadline_skip,
    !!d.outcome,
    !!d.accessibility || !!d.accessibility_skip,
  ];
  const csComplete = csFlags.every(Boolean);
  const flags = [
    !!d.hearing_date || !!d.time_unknown,
    !!d.dur_days || !!d.dur_hours || !!d.dur_minutes || !!d.dur_unknown,
    !!d.court_name || !!d.court_name_unknown,
    !!d.files || !!d.files_skip,
    !!d.about,
    !!d.hearing_type,
    !!d.court_decide || !!d.court_decide_unsure,
    csComplete,
    !!d.wants_prep,
  ];
  const visible = useSequentialReveal(flags);
  const csVisible = useSequentialReveal(csFlags);
  const H = ({ children }) => <h4 className="font-serif text-lg text-brand-gold">{children}</h4>;

  return (
    <div className="space-y-6" data-testid="route-a-form">
      {dialog}
      <SectionTitle>Hearing representation</SectionTitle>

      <Reveal show={visible > 0} scroll={false}>
        <div className="space-y-5">
          <H>Hearing details</H>
          <Field num={1} label="When is the hearing?" required={!d.hearing_date_unknown}>
            <div className="grid sm:grid-cols-2 gap-4">
              <TextField type="date" value={d.hearing_date} onChange={(v) => u("hearing_date", v)} disabled={d.hearing_date_unknown} testid="a-hearing-date" />
              <TextField type="time" value={d.hearing_time} onChange={(v) => u("hearing_time", v)} disabled={d.time_unknown} testid="a-hearing-time" />
            </div>
            <div className="flex gap-6 flex-wrap">
              <InlineCheck checked={d.hearing_date_unknown} onChange={(v) => set("routeA", { ...d, hearing_date_unknown: !!v, time_unknown: !!v })} label="I do not know the date" testid="a-hearing-date-unknown" />
              <InlineCheck checked={d.time_unknown} onChange={(v) => u("time_unknown", !!v)} label="I do not know the start time" testid="a-time-unknown" />
            </div>
          </Field>
        </div>
      </Reveal>

      <Reveal show={visible > 1}>
        <Field num={2} label="How long is the hearing expected to last?">
          <div className="grid grid-cols-3 gap-4">
            <div><span className="text-xs text-muted-foreground">Days</span><NumberField value={d.dur_days} onChange={(v) => u("dur_days", v)} disabled={d.dur_unknown} testid="a-dur-days" /></div>
            <div><span className="text-xs text-muted-foreground">Hours</span><NumberField value={d.dur_hours} onChange={(v) => u("dur_hours", v)} disabled={d.dur_unknown} testid="a-dur-hours" /></div>
            <div><span className="text-xs text-muted-foreground">Minutes</span><NumberField value={d.dur_minutes} onChange={(v) => u("dur_minutes", v)} disabled={d.dur_unknown} testid="a-dur-minutes" /></div>
          </div>
          <InlineCheck checked={d.dur_unknown} onChange={(v) => u("dur_unknown", !!v)} label="I do not know" testid="a-dur-unknown" />
        </Field>
      </Reveal>

      <Reveal show={visible > 2}>
        <div className="space-y-5">
          <Field num={3} label="Which court will hear the case?">
            <TextField value={d.court_name} onChange={(v) => u("court_name", v)} placeholder="Court name or hearing centre" disabled={d.court_name_unknown} testid="a-court-name" />
            <InlineCheck checked={d.court_name_unknown} onChange={(v) => u("court_name_unknown", !!v)} label="I do not know" testid="a-court-name-unknown" />
          </Field>
          <Field label="Case number" optional>
            <TextField value={d.case_number} onChange={(v) => u("case_number", v)} disabled={d.case_number_unknown} testid="a-case-number" />
            <InlineCheck checked={d.case_number_unknown} onChange={(v) => u("case_number_unknown", !!v)} label="I do not know" testid="a-case-number-unknown" />
          </Field>
          <Field label="Hearing format">
            <RadioField options={["Remote or video hearing", "In person", "Hybrid", "I do not know"]} value={d.format} onChange={(v) => u("format", v)} name="a-format" testid="a-format" />
          </Field>
        </div>
      </Reveal>

      <Reveal show={visible > 3}>
        <Field num={4} label="Upload the hearing notice or court order" optional hint={MAX_UPLOAD_HINT(maxMb)}>
          <FileUpload value={d.files} onChange={(v) => u("files", v)} maxMb={maxMb} testid="a-upload" />
          {!(d.files || d.files_skip) && (
            <Button type="button" variant="outline" onClick={() => u("files_skip", true)} data-testid="a-upload-continue" className="mt-3 rounded-sm border-brand-gold text-brand-black hover:bg-brand-gold/10">
              Continue / Skip this step
            </Button>
          )}
        </Field>
      </Reveal>

      <Reveal show={visible > 4}>
        <div className="space-y-5">
          <H>Nature of the hearing</H>
          <Field num={5} label="What is the hearing about?"><LongText value={d.about} onChange={(v) => u("about", v)} testid="a-about" /></Field>
        </div>
      </Reveal>

      <Reveal show={visible > 5}>
        <Field num={6} label="What type of hearing is it?">
          <RadioField options={HEARING_TYPES} value={d.hearing_type} onChange={(v) => u("hearing_type", v)} name="a-hearing-type" testid="a-hearing-type" after={{
            "Other — please state": d.hearing_type === "Other — please state" ? (
              <div className="pl-8">
                <TextField value={d.hearing_type_other} onChange={(v) => u("hearing_type_other", v)} placeholder="Please state *" testid="a-hearing-type-other" />
                {!(d.hearing_type_other && d.hearing_type_other.trim()) && <p className="text-xs text-brand-danger mt-1">Please complete this field.</p>}
              </div>
            ) : null,
          }} />
          {d.hearing_type === "Appeal or permission-to-appeal hearing" && (
            <InfoPanel tone="gold" testid="a-appeal-offer">
              <p className="mb-2">This sounds like it may relate to an appeal. We can create a linked appeal enquiry so the right team reviews it.</p>
              <InlineCheck checked={form.linked_route_d} onChange={(v) => set("linked_route_d", !!v)} label="Yes, create a linked appeal enquiry" testid="a-link-d" />
            </InfoPanel>
          )}
        </Field>
      </Reveal>

      <Reveal show={visible > 6}>
        <Field num={7} label="What do you understand the court may decide?">
          <LongText value={d.court_decide} onChange={(v) => u("court_decide", v)} disabled={d.court_decide_unsure} testid="a-court-decide" />
          <InlineCheck checked={d.court_decide_unsure} onChange={(v) => u("court_decide_unsure", !!v)} label="I am not sure" testid="a-court-decide-unsure" />
        </Field>
      </Reveal>

      <Reveal show={visible > 7}>
        <div className="space-y-5">
          <H>Case summary</H>
          <Field num={8} show={csVisible > 0} scroll={false} label="What has happened?">
            <LongText value={d.what_happened} onChange={(v) => u("what_happened", v)} testid="a-what-happened" />
          </Field>
          <Field show={csVisible > 1} label="What help would you like?">
            <LongText value={d.help_wanted} onChange={(v) => u("help_wanted", v)} testid="a-help-wanted" />
          </Field>
          <Field show={csVisible > 2} label="What is the next known hearing or deadline?">
            <TextField value={d.next_deadline} onChange={(v) => u("next_deadline", v)} testid="a-next-deadline" />
            {!(d.next_deadline || d.next_deadline_skip) && (
              <Button type="button" variant="outline" onClick={() => u("next_deadline_skip", true)} data-testid="a-next-deadline-continue" className="mt-3 rounded-sm border-brand-gold text-brand-black hover:bg-brand-gold/10">
                Continue / Skip this step
              </Button>
            )}
          </Field>
          <Field show={csVisible > 3} label="What outcome are you seeking?">
            <LongText value={d.outcome} onChange={(v) => u("outcome", v)} testid="a-outcome" />
          </Field>
          <Field show={csVisible > 4} label="Anything we should know about accessibility or communication needs?">
            <LongText value={d.accessibility} onChange={(v) => u("accessibility", v)} testid="a-accessibility" />
            {!(d.accessibility || d.accessibility_skip) && (
              <Button type="button" variant="outline" onClick={() => u("accessibility_skip", true)} data-testid="a-accessibility-continue" className="mt-3 rounded-sm border-brand-gold text-brand-black hover:bg-brand-gold/10">
                Continue / Skip this step
              </Button>
            )}
          </Field>
        </div>
      </Reveal>

      <Reveal show={visible > 8}>
        <div className="space-y-5">
          <H>Preparation assistance</H>
          <Field num={9} label="Do you also want help preparing for the hearing or managing the case?">
            <RadioField
              options={["Yes", "No", "I am not sure what preparation is needed"]}
              value={d.wants_prep}
              onChange={(v) => { u("wants_prep", v); set("wants_route_b", v === "Yes" || v.startsWith("I am not sure")); }}
              name="a-wants-prep"
              testid="a-wants-prep"
            />
            {d.wants_prep === "Yes" && (
              <div className="mt-3 space-y-2">
                <p className="text-sm font-semibold text-brand-black">What help do you need?</p>
                <CheckboxGroupField
                  options={["Preparing for the hearing only", "Managing the case only", "Both"]}
                  value={d.prep_scope}
                  onChange={(v) => {
                    const prev = d.prep_scope || [];
                    const IND = ["Preparing for the hearing only", "Managing the case only"];
                    let next = v || [];
                    const added = next.filter((x) => !prev.includes(x));
                    if (added.includes("Both")) {
                      next = next.filter((x) => !IND.includes(x));
                    } else if (added.some((x) => IND.includes(x)) && next.includes("Both")) {
                      next = next.filter((x) => x !== "Both");
                    }
                    if (next.includes("Preparing for the hearing only") && next.includes("Managing the case only")) {
                      next = [...next.filter((x) => !IND.includes(x)), "Both"];
                    }
                    u("prep_scope", next);
                  }}
                  testid="a-prep-scope"
                />
              </div>
            )}
            {(d.wants_prep === "Yes" || (d.wants_prep || "").startsWith("I am not sure")) && (
              <InfoPanel tone="gold" testid="a-prep-linked">
                We will create a linked preparation request and carry forward the details you have
                already provided. Our team will only ask about anything still outstanding.
              </InfoPanel>
            )}
          </Field>
        </div>
      </Reveal>

      {visible >= flags.length && !datesOk && (
        <RequiredDatesNote missing={missingDates} />
      )}
      <div className="flex items-center justify-between pt-6 border-t border-border">
        <Button variant="ghost" onClick={requestBack} data-testid={INTAKE.back} className="text-brand-darkgrey rounded-sm">Back</Button>
        {visible >= flags.length && (
          <Button onClick={onSubmit} disabled={submitting || !datesOk || !otherOk || !prepOk} data-testid={INTAKE.submit} className="bg-brand-black text-white hover:bg-brand-gold rounded-sm px-8">
            {submitting ? "Submitting…" : "Submit enquiry"}
          </Button>
        )}
      </div>
    </div>
  );
};
